import { axios } from './axiosClient';
import { validate } from '@/utils/errors/helpers';
import type { QSTAction } from '@/types/QSTActions';
import { getQSTActionSchema } from '@/types/schemas/questionnaires/getQSTAction';

import type {
  QstTableFilterData,
  Questionnaire,
  Question,
  QstListRow,
  QstList,
  QstQuestionnairePrototype,
} from '@/types/Questionnaires';
import {
  getQuestionnairesSchema,
  getQuestionnaireSchema,
} from '@/types/schemas/questionnaires/getQuestionnaires';
import { getQuestionSchema } from '@/types/schemas/questionnaires/getQuestions';
import type {
  QSTTransactionDetails,
  QstTransactionDetailsTableFilterData,
} from '@/types/QSTTransactionDetails';
import { getQSTTransactionDetailsSchema } from '@/types/schemas/questionnaires/getQSTTransactionDetails';
import { getListRowsSchema } from '@/types/schemas/questionnaires/getListRows';
import { getPrototypeForQuestionSchema } from '@/types/schemas/questionnaires/getPrototypeForQuestion';
import type { VisitLogin, VisitLoginTableFilterData } from '@/types/VisitLogin';
import {
  getVisitLoginSchema,
  getVisitLoginsSchema,
} from '@/types/schemas/questionnaires/getVisitLogins';

export async function getQSTActions(): Promise<QSTAction[]> {
  const response = await axios.get('/v2/questionnaires/actions');
  return validate(getQSTActionSchema, response.data.data);
}

export async function addOrEditActions(item: QSTAction) {
  const response = await axios.post('/v2/questionnaires/actions', item);
  if (response.status === 201) return true;
  return false;
}

export async function removeActionsByIds(ids: string[]) {
  const response = await axios.post('/v2/questionnaires/actions/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getQuestionnairesWithConditions({
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
  isActive,
  isStandards,
}: QstTableFilterData): Promise<{ data: Questionnaire[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(`/v2/questionnaires/search?take=${itemsPerPage}&skip=${skip}`, {
    searchText: query,
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isActive: isActive,
    isStandards,
  });
  const valid = validate(getQuestionnairesSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getQuestionnaireById(id: string) {
  const response = await axios.get(`/v2/questionnaires/${id}`, {
    validateStatus: (status: number) => status === 404 || status == 200,
  });
  if (response.status === 404) return null;
  return validate(getQuestionnaireSchema, response.data);
}

export async function addOrEditQuestionnaire(questionnaire: Questionnaire) {
  const response = await axios.post('/v2/questionnaires', questionnaire);
  if (response.status === 201) return true;
  return false;
}

export async function removeQuestionnairesByIds(ids: string[]) {
  const response = await axios.post('/v2/questionnaires/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getQuestionById(questionnaireId: string, id: string) {
  const response = await axios.get(`/v2/questionnaires/${questionnaireId}/questions/${id}`, {
    validateStatus: (status: number) => status === 404 || status == 200,
  });
  if (response.status === 404) return null;
  return validate(getQuestionSchema, response.data);
}

export async function addOrEditQuestion(questionnaireId: string, question: Question) {
  const response = await axios.post(`/v2/questionnaires/${questionnaireId}/questions`, question);
  if (response.status === 201) return true;
  return false;
}

export async function removeQuestionsByIds(questionnaireId: string, ids: string[]) {
  const response = await axios.post(
    `/v2/questionnaires/${questionnaireId}/questions/delete-by-ids`,
    ids,
    {
      validateStatus: (status: number) => status === 400 || status === 204,
    }
  );
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getVisitLoginsWithConditions({
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
  isActive,
  visitDateFrom,
  visitDateTo,
  deleted,
}: VisitLoginTableFilterData): Promise<{ data: VisitLogin[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/questionnaires/answers-report/visit-logins/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn: sortColumn,
      sortAscending: sortAscending,
      isActive: isActive,
      visitDateFrom,
      visitDateTo,
      deleted,
    }
  );
  const valid = validate(getVisitLoginsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getVisitLoginById(id: string): Promise<VisitLogin> {
  const response = await axios.get(`/v2/questionnaires/answers-report/visit-login/${id}`);
  return validate(getVisitLoginSchema, response.data);
}

export async function getReportsAnswersDetailsWithConditions({
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
  isActive,
  visitLoginId,
}: QstTransactionDetailsTableFilterData): Promise<{
  data: QSTTransactionDetails[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/questionnaires/answers-report/details/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn: sortColumn,
      sortAscending: sortAscending,
      isActive: isActive,
      visitLoginId,
    }
  );
  const valid = validate(getQSTTransactionDetailsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getQuestionListRows(
  questionnaireId: string,
  questionId: string
): Promise<QstListRow[] | null> {
  const response = await axios.get(
    `/v2/questionnaires/${questionnaireId}/questions/${questionId}/list-rows`,
    {
      validateStatus: (status: number) => status === 404 || status === 200,
    }
  );
  if (response.status === 404) return null;
  return validate(getListRowsSchema, response.data.data);
}

export async function addOrEditQuestionList(
  questionnaireId: string,
  questionId: string,
  list: QstList
) {
  const response = await axios.post(
    `/v2/questionnaires/${questionnaireId}/questions/${questionId}/list`,
    list
  );
  if (response.status === 201) return true;
  return false;
}

export async function addOrEditQuestionListRows(
  questionnaireId: string,
  questionId: string,
  listRows: QstListRow[]
) {
  const response = await axios.post(
    `/v2/questionnaires/${questionnaireId}/questions/${questionId}/list-rows`,
    listRows
  );
  if (response.status === 201) return true;
  return false;
}

export async function removeQuestionListRowsByIds(
  questionnaireId: string,
  questionId: string,
  ids: string[]
) {
  const response = await axios.post(
    `/v2/questionnaires/${questionnaireId}/questions/${questionId}/list-rows/delete-by-ids`,
    ids,
    {
      validateStatus: (status: number) => status === 400 || status === 204,
    }
  );
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getPrototypeForQuestion(
  questionId: string,
  bindingId: string,
  bindingType: number | null
): Promise<QstQuestionnairePrototype | null> {
  const response = await axios.get(
    `/v2/questionnaires/prototypes/${questionId}/question/${bindingId}/${bindingType}`,
    {
      validateStatus: (status: number) => status === 404 || status == 200,
    }
  );
  if (response.status === 404) return null;
  return validate(getPrototypeForQuestionSchema, response.data.data);
}

export async function savePrototypeForQuestion(prototype: QstQuestionnairePrototype) {
  const response = await axios.post(`/v2/questionnaires/prototypes`, prototype);
  if (response.status === 201) return true;
  return false;
}
